import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import {
  ContractKeySchema,
  DEFAULT_PAGE_LIMIT,
  NOTIFICATION_TIERS,
  NOTIFICATION_WATCH_TYPES,
  PartyKeySchema,
  type AcknowledgeNotificationRequest,
  type AcknowledgeNotificationResponse,
  type Notification,
  type NotificationListRequest,
  type NotificationListResponse,
  type NotificationTier,
  type NotificationWatchType,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { acknowledgeNotification, fetchNotifications } from '../api/notifications';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { CursorPager } from '../components/CursorPager';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import {
  EMPTY_CURSOR_STACK,
  pageNumber,
  popCursor,
  pushCursor,
  type CursorStack,
} from '../components/cursor-stack';
import { requireSurface } from '../routes/surfaces';

/**
 * Notifications — the ledger. O-1, FR-04, NFR-15; NFR-06, NFR-13. CLM-54.
 *
 * Needs attention and this surface read the same endpoint and ask different
 * questions. Needs attention asks *what needs acting on now* and omits
 * `include_acknowledged`. This surface is the ledger itself — every occurrence,
 * every tier, acknowledged or not, with the `(rule_id, contract_key,
 * occurrence_date, tier)` idempotency key visible on every row — which is what
 * an audit of the O-1 control reads (CLAUDE.md engagement rule 15). Without that
 * key a daily timer re-sends, people mute the channel, and O-1 fails silently
 * while dashboards stay green.
 *
 * ## Nothing here is derived that the API did not send
 *
 * Every column is a wire field of `NotificationSchema`; the filters are the
 * request parameters the contract supports and nothing else; the two totals are
 * the two the response carries. There is no client-side filter or sort over a
 * page (NFR-06 — scope is a server predicate and a browser filtering the twenty
 * rows it holds would be filtering a sample), no "channel", no "failed delivery"
 * (no such wire field), and after a write the page is **re-fetched**, never
 * patched, because acknowledgement state and totals are server-derived.
 *
 * ## The row key carries `sent_utc`
 *
 * Since migration 0082 several reminder slots of one tier share the four-tuple
 * wire key, so the React key appends `sent_utc`. Acknowledging the four-tuple
 * acknowledges every slot of that tier — CLM-28 locks the whole occurrence — and
 * the panel copy says so.
 *
 * ## Honest when the API is not there
 *
 * Production has no API until `deploy-api` is enabled (B-33), so the first call
 * rejects at network level and renders as `unknown`; a develop API built before
 * CLM-28 answers `501`. Both render the `ErrorNotice` plus a paragraph naming
 * `GET /v1/notifications`, and the filter form stays rendered. Never a blank
 * page, never an empty table presented as "no alerts".
 */

interface LedgerState {
  readonly response: NotificationListResponse | null;
  readonly busy: boolean;
  readonly problem: {
    readonly kind: ApiProblemKind;
    readonly detail: string | null;
    readonly correlation_id: string | null;
  } | null;
}

const INITIAL: LedgerState = { response: null, busy: true, problem: null };

/**
 * React key. `sent_utc` is appended because, after 0082, repeat slots of one
 * tier share the four-tuple wire key; the four-tuple alone would produce
 * duplicate keys on a ledger that includes repeats.
 */
function ledgerRowKey(n: Notification): string {
  const o = n.occurrence;
  return `${o.rule_id}|${o.contract_key}|${o.occurrence_date}|${o.tier}|${n.sent_utc}`;
}

function rowButtonId(n: Notification): string {
  return `nl-open-${ledgerRowKey(n)}`;
}

function toggle<T extends string>(list: readonly T[], value: T, on: boolean): readonly T[] {
  if (on) return list.includes(value) ? list : [...list, value];
  return list.filter((item) => item !== value);
}

/**
 * The paragraph both honest non-answers carry, naming the endpoint. The read
 * side explains why an empty ledger is not shown; the write side says only that
 * nothing was recorded, because the failure there is a lost acknowledgement,
 * not a false empty page.
 */
function EndpointNote({ side }: { readonly side: 'read' | 'write' }) {
  if (side === 'write') {
    return (
      <p className="mut">
        The endpoint is <code>POST /v1/notifications/acknowledge</code>. Nothing was recorded: the
        acknowledgement did not reach the server, so escalation for this tier continues until it
        does.
      </p>
    );
  }
  return (
    <p className="mut">
      The endpoint is <code>GET /v1/notifications</code>. An empty ledger here would claim nothing
      was ever sent — the O-1 control failing silently — so this page shows nothing until the
      endpoint answers.
    </p>
  );
}

/**
 * A typed key that is not a key is a form error, not a request (FR-14): the
 * contract schema would reject it before anything was sent, and rendering that
 * ZodError as "off-contract" would blame the gateway for a filter the user
 * typed. `null` means valid or empty.
 */
function keyFilterError(kind: 'contract' | 'party', draft: string): string | null {
  if (draft === '') return null;
  const parsed = kind === 'contract' ? ContractKeySchema.safeParse(draft) : PartyKeySchema.safeParse(draft);
  if (parsed.success) return null;
  return kind === 'contract'
    ? 'Not a contract key — expected PartyKey-nnnn, for example SGW-0012, or a key issued before 1 Oct 2026 such as SGW-MSA-0003. Not sent.'
    : 'Not a party key — expected three upper-case letters (P/V plus three for a partner or vendor), for example SGW. Not sent.';
}

export function Notifications() {
  const surface = requireSurface('notif');

  // Filters — every one is a request parameter (NFR-06). Text keys are held as
  // drafts and committed on blur / Enter so a keystroke is not a request.
  const [contractKeyDraft, setContractKeyDraft] = useState('');
  const [partyKeyDraft, setPartyKeyDraft] = useState('');
  const [contractKey, setContractKey] = useState('');
  const [partyKey, setPartyKey] = useState('');
  const [contractKeyError, setContractKeyError] = useState<string | null>(null);
  const [partyKeyError, setPartyKeyError] = useState<string | null>(null);
  const [watchTypes, setWatchTypes] = useState<readonly NotificationWatchType[]>([]);
  const [tiers, setTiers] = useState<readonly NotificationTier[]>([]);
  const [includeAcknowledged, setIncludeAcknowledged] = useState(true);

  const [stack, setStack] = useState<CursorStack>(EMPTY_CURSOR_STACK);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [state, setState] = useState<LedgerState>(INITIAL);
  /**
   * The opened row is held as a snapshot, not looked up in the current page:
   * the re-fetch after a 2xx (or a page/filter change while the panel is open)
   * may drop the row — "Include acknowledged" unchecked removes the row just
   * acknowledged — and the panel must stay open showing the receipt until
   * closed. The snapshot is refreshed from a re-fetched page only when a row
   * with the same key is still on it, so the acknowledged-by cell updates.
   */
  const [openRow, setOpenRow] = useState<Notification | null>(null);
  const openKey = openRow === null ? null : ledgerRowKey(openRow);
  /** Bumped to force a re-fetch of the same page — after every 2xx write, and the conflict Reload path. */
  const [reloadNonce, setReloadNonce] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const request: NotificationListRequest = {
      ...(contractKey === '' ? {} : { contract_key: contractKey }),
      ...(partyKey === '' ? {} : { party_key: partyKey }),
      // The contract requires min(1) when present, so none-checked omits the key.
      ...(watchTypes.length === 0 ? {} : { watch_type: [...watchTypes] }),
      ...(tiers.length === 0 ? {} : { tier: [...tiers] }),
      // Always sent, never omitted: the ledger's request is distinguishable from
      // Needs attention's (which omits the key) in a wire log.
      include_acknowledged: includeAcknowledged,
      page: cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor },
    };

    setState((previous) => ({ ...previous, busy: true }));

    void (async () => {
      try {
        const response = await fetchNotifications(request, controller.signal);
        if (controller.signal.aborted) return;
        setState({ response, busy: false, problem: null });
        setOpenRow((current) => {
          if (current === null) return current;
          const key = ledgerRowKey(current);
          return response.notifications.find((n) => ledgerRowKey(n) === key) ?? current;
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({
          response: null,
          busy: false,
          problem: {
            kind: classifyApiError(error),
            detail: error instanceof ApiError ? error.message : null,
            correlation_id: error instanceof ApiError ? error.correlation_id : null,
          },
        });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [contractKey, partyKey, watchTypes, tiers, includeAcknowledged, cursor, reloadNonce]);

  /** A filter change invalidates every cursor: they are minted per filter set. */
  const resetPaging = useCallback(() => {
    setStack(EMPTY_CURSOR_STACK);
    setCursor(undefined);
    setOpenRow(null);
  }, []);

  const commitTextFilters = useCallback(() => {
    const nextContract = contractKeyDraft.trim();
    const nextParty = partyKeyDraft.trim();
    // Validated here, against the same schemas the wrapper parses with, so a
    // malformed key is an inline form error and never a request (FR-14). The
    // committed filters are left as they are until both drafts are keys.
    const contractError = keyFilterError('contract', nextContract);
    const partyError = keyFilterError('party', nextParty);
    setContractKeyError(contractError);
    setPartyKeyError(partyError);
    if (contractError !== null || partyError !== null) return;
    if (nextContract === contractKey && nextParty === partyKey) return;
    setContractKey(nextContract);
    setPartyKey(nextParty);
    resetPaging();
  }, [contractKeyDraft, partyKeyDraft, contractKey, partyKey, resetPaging]);

  const onFilterSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    commitTextFilters();
  };

  const rows = state.response?.notifications ?? [];

  /**
   * Focus returns to the button that opened the panel (CLM-51). When the row
   * is no longer on the page — acknowledged and filtered out, or the page has
   * moved — the button is gone, so focus falls back to the ledger heading
   * rather than being dropped on `<body>`.
   */
  const closePanel = useCallback(() => {
    const key = openKey;
    setOpenRow(null);
    if (key === null) return;
    const target =
      document.getElementById(`nl-open-${key}`) ?? document.getElementById('nl-ledger-heading');
    target?.focus();
  }, [openKey]);

  const reload = useCallback(() => {
    setReloadNonce((n) => n + 1);
  }, []);

  const columns: readonly DataTableColumn<Notification>[] = [
    {
      id: 'key',
      header: 'Ledger key',
      cell: (n) => (
        // The four ratified columns of uq_notification_sent_occurrence, in
        // order. The fifth (slot_utc, 0082) is not on the wire; a repeat slot
        // says "reminder n" in the summary line the server built.
        <code>
          ({n.occurrence.rule_id},{' '}
          <Link to={`/contracts/record/${encodeURIComponent(n.occurrence.contract_key)}`}>
            {n.occurrence.contract_key}
          </Link>
          , {n.occurrence.occurrence_date}, {n.occurrence.tier})
        </code>
      ),
    },
    { id: 'about', header: 'About', cell: (n) => n.contract_summary_line },
    {
      id: 'watch',
      header: 'Watch',
      cell: (n) => (
        <>
          {n.watch_type}
          <br />
          <span className="mut">
            {n.days_ahead === null ? 'past due' : `${String(n.days_ahead)} d ahead`}
          </span>
        </>
      ),
    },
    { id: 'sent', header: 'Sent (UTC)', cell: (n) => n.sent_utc },
    {
      id: 'recipients',
      header: 'Recipients',
      cell: (n) => (
        <>
          {n.recipient_rules.map((rule) => (
            <span key={rule} className="tag out">
              {rule}
            </span>
          ))}
          <br />
          <span className="mut">{n.recipients.join(', ')}</span>
        </>
      ),
    },
    {
      id: 'ack',
      header: 'Acknowledged',
      cell: (n) =>
        n.acknowledged_utc === null ? (
          // The word, not only the colour.
          <span className="tag bad">Open</span>
        ) : (
          <>
            {n.acknowledged_by_upn}
            <br />
            <span className="mut">{n.acknowledged_utc}</span>
          </>
        ),
    },
  ];

  const totals = state.response?.totals ?? null;
  const page = state.response?.page ?? null;
  const problem = state.problem;

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      <div className="card">
        <h3 id="nl-ledger-heading" tabIndex={-1}>
          Notification ledger
        </h3>
        <p>
          The ledger — every occurrence and tier, acknowledged or not. For what needs acting on
          now, use <Link to="/contracts/attention">Needs attention</Link>.
        </p>

        {/*
         * Always rendered, including in the problem state: the form is the
         * surface's primary control and a page that is only an alert is blank.
         */}
        <form className="vfilters" aria-label="Ledger filters" onSubmit={onFilterSubmit}>
          <label className="check">
            Contract key
            <input
              type="text"
              name="contract_key"
              autoComplete="off"
              value={contractKeyDraft}
              aria-invalid={contractKeyError !== null}
              aria-describedby={contractKeyError === null ? undefined : 'nl-contract-key-error'}
              onChange={(event) => setContractKeyDraft(event.target.value)}
              onBlur={commitTextFilters}
            />
            {contractKeyError !== null ? (
              <span id="nl-contract-key-error" className="ferr">
                {contractKeyError}
              </span>
            ) : null}
          </label>
          <label className="check">
            Party key
            <input
              type="text"
              name="party_key"
              autoComplete="off"
              value={partyKeyDraft}
              aria-invalid={partyKeyError !== null}
              aria-describedby={partyKeyError === null ? undefined : 'nl-party-key-error'}
              onChange={(event) => setPartyKeyDraft(event.target.value)}
              onBlur={commitTextFilters}
            />
            {partyKeyError !== null ? (
              <span id="nl-party-key-error" className="ferr">
                {partyKeyError}
              </span>
            ) : null}
          </label>
          <fieldset>
            <legend>Watch type</legend>
            {NOTIFICATION_WATCH_TYPES.map((watchType) => (
              <label key={watchType} className="check">
                <input
                  type="checkbox"
                  checked={watchTypes.includes(watchType)}
                  onChange={(event) => {
                    setWatchTypes((current) => toggle(current, watchType, event.target.checked));
                    resetPaging();
                  }}
                />
                {watchType}
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>Tier</legend>
            {NOTIFICATION_TIERS.map((tier) => (
              <label key={tier} className="check">
                <input
                  type="checkbox"
                  checked={tiers.includes(tier)}
                  onChange={(event) => {
                    setTiers((current) => toggle(current, tier, event.target.checked));
                    resetPaging();
                  }}
                />
                {tier}
              </label>
            ))}
          </fieldset>
          <label className="check">
            <input
              type="checkbox"
              checked={includeAcknowledged}
              onChange={(event) => {
                setIncludeAcknowledged(event.target.checked);
                resetPaging();
              }}
            />
            Include acknowledged
          </label>
          {/* Enter in a text input submits; the button is the same commit for pointer users. */}
          <button type="submit" className="btn sm">
            Apply key filters
          </button>
        </form>

        {problem ? (
          <>
            <ErrorNotice
              kind={problem.kind}
              detail={problem.detail}
              correlation_id={problem.correlation_id}
            />
            {problem.kind === 'not_implemented' || problem.kind === 'unknown' ? (
              <EndpointNote side="read" />
            ) : null}
          </>
        ) : (
          <>
            {state.response === null ? (
              <p role="status" aria-busy="true">
                Reading the ledger…
              </p>
            ) : null}

            {totals ? (
              <>
                <div className="totals">
                  <div className="total">
                    <span>Unacknowledged</span>
                    <b>{totals.unacknowledged}</b>
                  </div>
                  <div className="total" data-tone={totals.past_due > 0 ? 'bad' : 'plain'}>
                    <span>Past due</span>
                    <b>{totals.past_due}</b>
                  </div>
                </div>
                <p className="mut">
                  Open rows in your scope, regardless of the include-acknowledged filter — the
                  server counts them so.
                </p>
              </>
            ) : null}

            {state.response !== null ? (
              <DataTable
                caption="Notification ledger — every occurrence and tier in your scope, newest occurrence first"
                columns={columns}
                rows={rows}
                rowKey={ledgerRowKey}
                isCurrent={(n) => ledgerRowKey(n) === openKey}
                busy={state.busy}
                activate={{
                  header: 'Action',
                  // Decided by the row's own state, never by the caller's role
                  // claim: a 403 is a presented state, not a hidden button.
                  label: (n) => (n.acknowledged_utc === null ? 'Acknowledge' : 'View'),
                  buttonId: rowButtonId,
                  onActivate: (n) => {
                    setOpenRow(n);
                  },
                }}
                empty={
                  <>
                    <p>
                      <strong>No ledger rows match in your scope.</strong>
                    </p>
                    <p>
                      The ledger records what was sent; an empty page means nothing has been sent
                      for these filters — or nothing in your scope has — and this page cannot tell
                      you which.
                    </p>
                  </>
                }
              />
            ) : null}

            {page ? (
              <CursorPager
                next_cursor={page.next_cursor}
                historyDepth={stack.cursors.length}
                busy={state.busy}
                pageNumber={pageNumber(stack)}
                returnedCount={page.returned_count}
                totalInScope={page.total_in_scope}
                onNext={() => {
                  const next = page.next_cursor;
                  if (next === null) return;
                  setStack((current) => pushCursor(current, next));
                  setCursor(next);
                  setOpenRow(null);
                }}
                onPrevious={() => {
                  const popped = popCursor(stack);
                  setStack(popped.stack);
                  setCursor(popped.cursor);
                  setOpenRow(null);
                }}
              />
            ) : null}
          </>
        )}
      </div>

      {openRow ? (
        <AcknowledgePanel
          row={openRow}
          rowKey={ledgerRowKey(openRow)}
          onClose={closePanel}
          onWritten={reload}
        />
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Acknowledge panel                                                          */
/* -------------------------------------------------------------------------- */

type PanelPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting' }
  | { readonly kind: 'settled'; readonly response: AcknowledgeNotificationResponse }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

/**
 * The write side, in-page below the table (VerifyPanel precedent), no sub-route.
 *
 * Posts the row's own `occurrence` object — the idempotency key — with an
 * optional note, and when the response reports `remaining_open_tiers` offers to
 * clear the rest with `acknowledge_all_tiers_for_occurrence: true`. Every 2xx
 * calls `onWritten` so the surface re-fetches; nothing is patched locally.
 *
 * A replay (`was_already_acknowledged: true`) is rendered as a status, not an
 * alert: the existing acknowledgement is returned unchanged, no audit row was
 * written, and `audit.audit_id` reads `replay:<correlation>` — a receipt of the
 * replay, not evidence of a new write. The copy does not claim otherwise.
 */
function AcknowledgePanel({
  row,
  rowKey,
  onClose,
  onWritten,
}: {
  readonly row: Notification;
  readonly rowKey: string;
  readonly onClose: () => void;
  /** Called after every 2xx so the ledger page is re-fetched. */
  readonly onWritten: () => void;
}) {
  const [phase, setPhase] = useState<PanelPhase>({ kind: 'idle' });
  const [note, setNote] = useState('');

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Focus moves to the heading on open (CLM-51); state resets per row.
  useEffect(() => {
    headingRef.current?.focus();
    setPhase({ kind: 'idle' });
    setNote('');
  }, [rowKey]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  function submit(allTiers: boolean) {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase({ kind: 'submitting' });

    const trimmed = note.trim();
    const request: AcknowledgeNotificationRequest = {
      occurrence: row.occurrence,
      ...(trimmed === '' ? {} : { note: trimmed }),
      ...(allTiers ? { acknowledge_all_tiers_for_occurrence: true } : {}),
    };

    void (async () => {
      try {
        const response = await acknowledgeNotification(request, controller.signal);
        if (controller.signal.aborted) return;
        setPhase({ kind: 'settled', response });
        onWritten();
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          detail: error instanceof ApiError ? error.message : null,
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      }
    })();
  }

  const submitting = phase.kind === 'submitting';
  const isOpen = row.acknowledged_utc === null;
  const o = row.occurrence;
  const keyText = `${o.contract_key} ${o.tier} ${o.occurrence_date}`;

  return (
    <section className="vpanel" aria-labelledby="nl-panel-heading" aria-busy={submitting}>
      <h3 id="nl-panel-heading" ref={headingRef} tabIndex={-1}>
        {isOpen ? 'Acknowledge' : 'Ledger row'} {keyText}
      </h3>
      <p className="vsub">
        <code>
          ({o.rule_id}, {o.contract_key}, {o.occurrence_date}, {o.tier})
        </code>
      </p>
      <p>{row.contract_summary_line}</p>
      <p className="mut">
        Tier <code>{o.tier}</code> ·{' '}
        {isOpen ? (
          <>
            <span className="tag bad">Open</span> — acknowledging this tier acknowledges every
            reminder slot sent for it on this date.
          </>
        ) : (
          <>
            Acknowledged by {row.acknowledged_by_upn} at {row.acknowledged_utc}.
          </>
        )}
      </p>

      {isOpen && phase.kind !== 'settled' ? (
        <div className="vform">
          <div className="field">
            <textarea
              aria-label="Note (optional)"
              rows={3}
              maxLength={1000}
              value={note}
              disabled={submitting}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <div className="vacts">
            <button
              type="button"
              className="btn pri"
              disabled={submitting}
              onClick={() => {
                submit(false);
              }}
            >
              Acknowledge this tier
            </button>
            <button type="button" className="btn qt" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      ) : (
        <div className="vacts">
          <button type="button" className="btn qt" onClick={onClose}>
            Close
          </button>
        </div>
      )}

      {/* Announced politely, after the user's own keystroke, never on top of it. */}
      <div className="voutcome" aria-live="polite">
        {phase.kind === 'submitting' ? <p>Recording the acknowledgement…</p> : null}

        {phase.kind === 'settled' && !phase.response.was_already_acknowledged ? (
          <div className="banner up" role="status">
            <p className="notice-head">
              <strong>
                Acknowledged by{' '}
                {phase.response.acknowledged[0]?.acknowledged_by_upn ??
                  phase.response.audit.actor_upn}{' '}
                at{' '}
                {phase.response.acknowledged[0]?.acknowledged_utc ??
                  phase.response.audit.occurred_at_utc}
                .
              </strong>
            </p>
            <p>
              Recorded as <code>{phase.response.audit.audit_id}</code>. Escalation for this tier
              has stopped; the page below has been re-read from the server.
            </p>
            {phase.response.remaining_open_tiers.length > 0 ? (
              <>
                <p>
                  Still open for this date: {phase.response.remaining_open_tiers.join(', ')}.
                </p>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    submit(true);
                  }}
                >
                  Acknowledge all open tiers for this date
                </button>
              </>
            ) : null}
          </div>
        ) : null}

        {phase.kind === 'settled' && phase.response.was_already_acknowledged ? (
          <div className="banner up" role="status">
            <p className="notice-head">
              <strong>
                Already acknowledged by {phase.response.acknowledged[0]?.acknowledged_by_upn ?? '—'}{' '}
                at {phase.response.acknowledged[0]?.acknowledged_utc ?? '—'}; nothing was changed.
              </strong>
            </p>
            <p>
              Receipt <code>{phase.response.audit.audit_id}</code>. The existing acknowledgement
              stands; no new audit row was written.
            </p>
          </div>
        ) : null}

        {phase.kind === 'problem' ? (
          <ErrorNotice
            kind={phase.problem}
            detail={
              phase.problem === 'not_found'
                ? 'This occurrence is not in the ledger or is outside your scope.'
                : phase.detail
            }
            correlation_id={phase.correlation_id}
            action={
              phase.problem === 'conflict' ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    // Re-read the page from the server; nothing is patched locally.
                    onWritten();
                    onClose();
                  }}
                >
                  Reload
                </button>
              ) : null
            }
          />
        ) : null}
        {phase.kind === 'problem' &&
        (phase.problem === 'not_implemented' || phase.problem === 'unknown') ? (
          <EndpointNote side="write" />
        ) : null}
      </div>
    </section>
  );
}
